import { useNavigate } from 'react-router-dom';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Badge,
  Checkbox,
  Input,
  Select,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  Button,
} from '@travellegacy/nexops-design-system';
import {
  Search,
  ListChecks,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  X,
} from 'lucide-react';
import type { Demand } from '../api/demands.api';
import { useDemandsFacets } from '../hooks/useDemands';
import { formatDurationRich } from '../utils/format-duration';
import { buildNotionPageUrl } from '../utils/notion-url';
import { formatDate, formatRelativeTime } from '@/shared/lib/utils';

interface Props {
  demands: Demand[];
  total: number;
  page: number;
  totalPages: number;
  isLoading: boolean;
  search: string;
  onSearchChange: (value: string) => void;
  onPageChange: (page: number) => void;
  filterType: string;
  filterStatus: string;
  filterResponsible: string;
  filterActive: '' | 'true' | 'false';
  filterHasRework: boolean;
  onFilterTypeChange: (value: string) => void;
  onFilterStatusChange: (value: string) => void;
  onFilterResponsibleChange: (value: string) => void;
  onFilterActiveChange: (value: '' | 'true' | 'false') => void;
  onFilterHasReworkChange: (value: boolean) => void;
  onClearFilters: () => void;
}

export function DemandsTable({
  demands,
  total,
  page,
  totalPages,
  isLoading,
  search,
  onSearchChange,
  onPageChange,
  filterType,
  filterStatus,
  filterResponsible,
  filterActive,
  filterHasRework,
  onFilterTypeChange,
  onFilterStatusChange,
  onFilterResponsibleChange,
  onFilterActiveChange,
  onFilterHasReworkChange,
  onClearFilters,
}: Props) {
  const navigate = useNavigate();
  const facetsQuery = useDemandsFacets();
  const facets = facetsQuery.data?.data;

  const typeOptions = [
    { value: '', label: 'Todos os tipos' },
    ...(facets?.types ?? []).map((t) => ({ value: t, label: t })),
  ];
  const statusOptions = [
    { value: '', label: 'Todos os status' },
    ...(facets?.statuses ?? []).map((s) => ({ value: s, label: s })),
  ];
  const responsibleOptions = [
    { value: '', label: 'Todos os responsáveis' },
    ...(facets?.responsibles ?? []).map((r) => ({ value: r, label: r })),
  ];
  const activeOptions = [
    { value: '', label: 'Todas' },
    { value: 'true', label: 'Ativas' },
    { value: 'false', label: 'Inativas' },
  ];

  const hasActiveFilters =
    !!filterType ||
    !!filterStatus ||
    !!filterResponsible ||
    filterActive !== '' ||
    filterHasRework;

  function openInNotion(notionPageId: string) {
    const url = buildNotionPageUrl(notionPageId);
    if (url) window.open(url, '_blank', 'noopener,noreferrer');
  }

  function openDetail(id: string) {
    navigate(`/demands/${id}`);
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <CardTitle className="flex items-center gap-2">
            <ListChecks className="h-5 w-5" />
            Todas as demandas
            <Badge variant="outline">{total}</Badge>
          </CardTitle>
          <div className="relative w-full max-w-xs">
            <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => onSearchChange(e.target.value)}
              placeholder="Buscar por título ou responsável…"
              className="pl-8"
            />
          </div>
        </div>

        {/* Filtros avançados */}
        <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
          <Select
            value={filterType}
            onChange={(e) => onFilterTypeChange(e.target.value)}
            options={typeOptions}
          />
          <Select
            value={filterStatus}
            onChange={(e) => onFilterStatusChange(e.target.value)}
            options={statusOptions}
          />
          <Select
            value={filterResponsible}
            onChange={(e) => onFilterResponsibleChange(e.target.value)}
            options={responsibleOptions}
          />
          <Select
            value={filterActive}
            onChange={(e) =>
              onFilterActiveChange(e.target.value as '' | 'true' | 'false')
            }
            options={activeOptions}
          />
          <div className="flex items-center justify-between gap-2">
            <Checkbox
              id="filter-has-rework"
              label="Com retrabalho"
              checked={filterHasRework}
              onChange={onFilterHasReworkChange}
            />
            {hasActiveFilters && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onClearFilters}
                title="Limpar filtros"
              >
                <X className="h-3 w-3" />
                Limpar
              </Button>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <p className="text-sm text-muted-foreground">Carregando…</p>
        ) : demands.length === 0 ? (
          <p className="text-sm text-muted-foreground">Nenhuma demanda encontrada.</p>
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Título</TableHead>
                  <TableHead>Responsável</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Ciclos</TableHead>
                  <TableHead className="text-right">Tempo total</TableHead>
                  <TableHead>Última atividade</TableHead>
                  <TableHead className="w-10">
                    <span className="sr-only">Abrir</span>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {demands.map((d) => (
                  <TableRow
                    key={d.id}
                    onClick={() => openDetail(d.id)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        openDetail(d.id);
                      }
                    }}
                    tabIndex={0}
                    role="link"
                    aria-label={`Ver detalhes de "${d.title}"`}
                    title="Ver detalhes"
                    className="cursor-pointer transition-colors hover:bg-muted/50 focus-visible:bg-muted/50 focus-visible:outline-none"
                  >
                    <TableCell className="max-w-md truncate font-medium">{d.title}</TableCell>
                    <TableCell>{d.responsibleName}</TableCell>
                    <TableCell>
                      {d.type ? (
                        <Badge variant="info">{d.type}</Badge>
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant={d.isActive ? 'success' : 'outline'}>
                        {d.currentStatus}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{d.cyclesCount}</TableCell>
                    <TableCell className="text-right tabular-nums">
                      {formatDurationRich(d.totalActiveDurationMs)}
                    </TableCell>
                    <TableCell
                      className="text-xs text-muted-foreground"
                      title={formatDate(d.lastEventAt)}
                    >
                      {formatRelativeTime(d.lastEventAt)}
                    </TableCell>
                    <TableCell className="text-right">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          openInNotion(d.notionPageId);
                        }}
                        className="inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        aria-label={`Abrir "${d.title}" no Notion`}
                        title="Abrir no Notion"
                      >
                        <ExternalLink className="h-4 w-4" aria-hidden="true" />
                      </button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {totalPages > 1 && (
              <div className="mt-4 flex items-center justify-between">
                <p className="text-sm text-muted-foreground">
                  Página {page} de {totalPages}
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page <= 1}
                    onClick={() => onPageChange(page - 1)}
                  >
                    <ChevronLeft className="h-4 w-4" />
                    Anterior
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page >= totalPages}
                    onClick={() => onPageChange(page + 1)}
                  >
                    Próxima
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
